import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import axios from "../../api/axios_client";
import { Switch, FormControlLabel } from "@mui/material";
import { Select as NewSelect } from "react-select";

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const countries = [
    "Algeria",
    "Bahrain",
    "Comoros",
    "Djibouti",
    "Egypt",
    "Iraq",
    "Jordan",
    "Kuwait",
    "Lebanon",
    "Libya",
    "Mauritania",
    "Morocco",
    "Oman",
    "Palestine",
    "Qatar",
    "Saudi Arabia",
    "Somalia",
    "Sudan",
    "Syria",
    "Tunisia",
    "United Arab Emirates",
    "Yemen",
  ];

  const agriculturalFields = [
    "Agriculture",
    "Disaster Management",
    "Climate Monitoring",
    "Urban Planning",
    "Weather Forecasting",
    "Forest Mapping",
    "Water Management",
  ];

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`/api/admin/users/${userId}`);
      setUser(response.data);
      setEditedUser(response.data);
    } catch (error) {
      toast.error("Failed to fetch user details");
      navigate("/admin/users");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
  setSaveLoading(true);
  try {
    await axios.put(`/api/admin/users/${userId}`, editedUser);
    setUser(editedUser);
    setEditing(false);
    toast.success("User updated successfully");
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to update user";
    toast.error(errorMessage);
  } finally {
    setSaveLoading(false);
    setSaveDialog(false); 
  }
};

  const handleDelete = async () => {
  setDeleteLoading(true);
  try {
    await axios.delete(`/api/admin/users`, { data: { ids: [userId] } });
    toast.success("User deleted successfully");
    navigate("/admin/users");
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to delete user";
    toast.error(errorMessage);
  } finally {
    setDeleteLoading(false);
    setDeleteDialog(false); 
  }
};

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/admin/users")}
        sx={{ mb: 3 }}
      >
        Back to Users List
      </Button>

      <Card>
        <CardContent>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">User Profile</Typography>
            <Box>
              {editing ? (
                <>
                  <Button onClick={() => setEditing(false)} sx={{ mr: 1 }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setSaveDialog(true)}
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <>
                  {user.access === "2" && (
                    <Button onClick={() => setEditing(true)} sx={{ mr: 1 }}>
                      Edit
                    </Button>
                  )}
                  {user.access !== "3" && (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialog(true)}
                    >
                      Delete User
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Account Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Account Information
              </Typography>
              <TextField
                fullWidth
                label="Username"
                value={editing ? editedUser.username : user.username}
                onChange={(e) =>
                  setEditedUser({ ...editedUser, username: e.target.value })
                }
                disabled={!editing}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                value={editing ? editedUser.email : user.email}
                onChange={(e) =>
                  setEditedUser({ ...editedUser, email: e.target.value })
                }
                disabled={!editing}
                margin="normal"
              />
              {editing && (
                <TextField
                  fullWidth
                  label="Reset Password"
                  type="password"
                  value={editedUser.password || ""}
                  onChange={(e) =>
                    setEditedUser({ ...editedUser, password: e.target.value })
                  }
                  margin="normal"
                  helperText="Leave blank to keep current password"
                />
              )}
              <FormControl fullWidth margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  value={editing ? editedUser.access : user.access}
                  onChange={(e) =>
                    setEditedUser({ ...editedUser, access: e.target.value })
                  }
                  disabled={true} // Always disabled to prevent role changes
                  label="Role"
                >
                  <MenuItem value="1">User</MenuItem>
                  <MenuItem value="2">Country Admin</MenuItem>
                  <MenuItem value="3">Super Admin</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={editing ? editedUser.active : user.active}
                    onChange={(e) =>
                      setEditedUser({ ...editedUser, active: e.target.checked })
                    }
                    disabled={!editing}
                    color="primary"
                  />
                }
                label={`User is ${
                  (editing ? editedUser.active : user.active)
                    ? "Active"
                    : "Inactive"
                }`}
                sx={{ mt: 2 }}
              />
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Personal Information
              </Typography>
              <TextField
                fullWidth
                label="First Name"
                value={editing ? editedUser.first_name : user.first_name}
                onChange={(e) =>
                  setEditedUser({ ...editedUser, first_name: e.target.value })
                }
                disabled={!editing}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Middle Name"
                value={editing ? editedUser.middle_name : user.middle_name}
                onChange={(e) =>
                  setEditedUser({ ...editedUser, middle_name: e.target.value })
                }
                disabled={!editing}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={editing ? editedUser.last_name : user.last_name}
                onChange={(e) =>
                  setEditedUser({ ...editedUser, last_name: e.target.value })
                }
                disabled={!editing}
                margin="normal"
              />
              {user.access !== "1" && (
                <>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={
                      editing ? editedUser.phone_number : user.phone_number
                    }
                    onChange={(e) =>
                      setEditedUser({
                        ...editedUser,
                        phone_number: e.target.value,
                      })
                    }
                    disabled={!editing}
                    margin="normal"
                  />
                  <small
                    style={{
                      color: "#666",
                      fontSize: "0.8em",
                      display: "block",
                    }}
                  >
                    Please include country code (e.g., +962)
                  </small>
                </>
              )}
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12}>
              {user.access !== "3" && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Professional Information
                </Typography>
              )}
              <Grid container spacing={3}>
                {user.access === "2" && (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={
                          editing ? editedUser.country : user.country || ""
                        }
                        onChange={(e) =>
                          setEditedUser({
                            ...editedUser,
                            country: e.target.value,
                          })
                        }
                        disabled={!editing}
                        label="Country"
                      >
                        {countries.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {user.access === "2" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Position"
                      value={
                        editing ? editedUser.position : user.position || ""
                      }
                      onChange={(e) =>
                        setEditedUser({
                          ...editedUser,
                          position: e.target.value,
                        })
                      }
                      disabled={!editing}
                      margin="normal"
                    />
                  </Grid>
                )}
                {user.access === "1" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Organization"
                      value={
                        editing
                          ? editedUser.organization
                          : user.organization || ""
                      }
                      onChange={(e) =>
                        setEditedUser({
                          ...editedUser,
                          organization: e.target.value,
                        })
                      }
                      disabled={!editing}
                      margin="normal"
                    />
                  </Grid>
                )}
                {user.access === "1" && (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel shrink>Fields of Expertise</InputLabel>
                      <Select
                        disabled={!editing}
                        isMulti
                        name="fields"
                        value={
                          editing
                            ? editedUser.fields?.map((f) => ({
                                value: f,
                                label: f,
                              }))
                            : user.fields?.map((f) => ({ value: f, label: f }))
                        }
                        onChange={(selectedOptions) => {
                          setEditedUser({
                            ...editedUser,
                            fields: selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [],
                          });
                        }}
                        options={agriculturalFields.map((field) => ({
                          value: field,
                          label: field,
                        }))}
                        isDisabled={!editing}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: "56px",
                            marginTop: "16px",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {saveDialog && (
        <Dialog
          open={saveDialog}
          onClose={saveLoading ? undefined : () => setSaveDialog(false)}
        >
          <DialogTitle>Confirm Changes</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save these changes?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setSaveDialog(false)}
              disabled={saveLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveLoading}
              startIcon={
                saveLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : null
              }
            >
              {saveLoading ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {deleteDialog && (
        <Dialog
          open={deleteDialog}
          onClose={deleteLoading ? undefined : () => setDeleteDialog(false)}
        >
          <DialogTitle sx={{ color: "#dc3545" }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this user?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setDeleteDialog(false)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : null
              }
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UserProfilePage;
